import { useEffect, useState } from 'react'
import { FormWrapper } from '@/form-control'
import { CheckboxField } from '@/form-control/fields'
import ROUTES from '@/ROUTES'

import { IAppSettings } from '@/MODELS/appSettings.model'

import { useResource } from '@/services/resource.service'

import AdminPageHeader from '@/components/AdminPageHeader'
import Loading from '@/components/Loading'

export default function AdminAppSettings() {
    const [appSettings] = useResource<IAppSettings[]>(ROUTES.admin.appSettings)
    const [settingsId, setSettingsId] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (appSettings && appSettings.length > 0) {
            setSettingsId(appSettings[0]._id)
            setLoading(false)
        } else if (appSettings && appSettings.length === 0) {
            setSettingsId('new')
            setLoading(false)
        }
    }, [appSettings])

    if (loading) return <Loading />

    return (
        <FormWrapper<IAppSettings>
            endpoint={ROUTES.admin.appSettings}
            submitButtonText='Save Settings'
            id={settingsId}
            callbackAfterSubmit={() => {}}
            className='p-6 sm:p-10'
            submitButtonClass='bg-brand-600'
        >
            {(f, { formValues, setFormValues }) => (
                <div className='space-y-6'>
                    <AdminPageHeader
                        heading='App Settings'
                        subheading='Configure application-wide settings'
                    />

                    <div className='bg-blue-50 border border-blue-200 rounded-xl p-6'>
                        <h3 className='text-sm font-semibold text-blue-800 mb-1'>NOTE: Some app settings can be overridden at the User level!</h3>
                        <p className='text-sm text-blue-700'>
                            The Paywall and Stripe Mode settings below are the <strong>defaults for all users</strong>. Individual
                            users can be marked as "Test Users" in the user editor (Admin &rarr; Users &rarr; Edit), which gives them their
                            own Paywall and Stripe Mode settings that override these defaults. Use this for internal testing, demo
                            accounts, or accounts that need different Stripe environments.
                        </p>
                    </div>

                    <div className='bg-white rounded-xl shadow-sm border border-neutral-200 p-6'>
                        <h3 className='text-lg font-semibold text-gray-900 mb-1'>Subscription Paywall</h3>
                        <p className='text-sm text-gray-500 mb-4'>
                            When enabled, users must have an active Stripe subscription to access the app. When disabled, all features are free.
                        </p>
                        <CheckboxField
                            value={formValues.paywallEnabled ?? false}
                            onChange={(value) => setFormValues({ ...formValues, paywallEnabled: value })}
                            label='Enable Paywall'
                        />
                    </div>

                    {formValues.paywallEnabled && (
                        <div className='bg-white rounded-xl shadow-sm border border-neutral-200 p-6'>
                            <h3 className='text-lg font-semibold text-gray-900 mb-1'>Paywall Mode</h3>
                            <p className='text-sm text-gray-500 mb-4'>
                                Choose when users are prompted to subscribe.
                            </p>

                            <div className='flex gap-3'>
                                <button
                                    type='button'
                                    onClick={() => setFormValues({ ...formValues, paywallMode: 'trial' })}
                                    className={`flex-1 rounded-lg border-2 p-4 text-left transition-all ${
                                        (formValues.paywallMode ?? 'trial') === 'trial'
                                            ? 'border-brand-500 bg-brand-50'
                                            : 'border-neutral-200 bg-white hover:border-neutral-300'
                                    }`}
                                >
                                    <div className='flex items-center gap-2 mb-1'>
                                        <div className={`w-3 h-3 rounded-full ${
                                            (formValues.paywallMode ?? 'trial') === 'trial' ? 'bg-brand-500' : 'bg-neutral-300'
                                        }`} />
                                        <span className='font-semibold text-gray-900'>Time-Based Trial</span>
                                    </div>
                                    <p className='text-sm text-gray-500'>
                                        Users subscribe immediately and get a free trial period. Stripe manages the trial countdown.
                                    </p>
                                </button>

                                <button
                                    type='button'
                                    onClick={() => setFormValues({ ...formValues, paywallMode: 'usage' })}
                                    className={`flex-1 rounded-lg border-2 p-4 text-left transition-all ${
                                        formValues.paywallMode === 'usage'
                                            ? 'border-brand-500 bg-brand-50'
                                            : 'border-neutral-200 bg-white hover:border-neutral-300'
                                    }`}
                                >
                                    <div className='flex items-center gap-2 mb-1'>
                                        <div className={`w-3 h-3 rounded-full ${
                                            formValues.paywallMode === 'usage' ? 'bg-brand-500' : 'bg-neutral-300'
                                        }`} />
                                        <span className='font-semibold text-gray-900'>Value-Based</span>
                                    </div>
                                    <p className='text-sm text-gray-500'>
                                        Users get free access until they hit a usage threshold, then are prompted to subscribe with no trial.
                                    </p>
                                </button>
                            </div>

                            {/* Time-Based Trial: trial period days config */}
                            {(formValues.paywallMode ?? 'trial') === 'trial' && (
                                <div className='mt-5 pt-5 border-t border-neutral-200'>
                                    <h4 className='text-sm font-semibold text-gray-900 mb-1'>Free Trial Period</h4>
                                    <p className='text-sm text-gray-500 mb-3'>
                                        How many days new subscribers can use the app for free before payment is required.
                                        Only applies to new subscriptions.
                                    </p>
                                    <div className='flex items-center gap-3'>
                                        <input
                                            type='number'
                                            min={0}
                                            max={730}
                                            value={formValues.trialPeriodDays ?? 30}
                                            onChange={(e) => setFormValues({ ...formValues, trialPeriodDays: parseInt(e.target.value) || 0 })}
                                            className='w-24 rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500'
                                        />
                                        <span className='text-sm text-gray-600'>days</span>
                                    </div>
                                    {(formValues.trialPeriodDays ?? 30) === 0 && (
                                        <div className='mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg'>
                                            <p className='text-sm text-amber-700 font-medium'>
                                                Trial is disabled. New subscribers will be charged immediately. The subscription modal will
                                                show "Subscribe Now" instead of "Start Your Free Trial."
                                            </p>
                                        </div>
                                    )}
                                    {(formValues.trialPeriodDays ?? 30) > 0 && (
                                        <p className='mt-2 text-xs text-gray-400'>
                                            New users will see "{formValues.trialPeriodDays ?? 30}-day free trial" messaging and can subscribe without a credit card.
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Value-Based: usage thresholds config */}
                            {formValues.paywallMode === 'usage' && (
                                <div className='mt-5 pt-5 border-t border-neutral-200'>
                                    <h4 className='text-sm font-semibold text-gray-900 mb-1'>Usage Thresholds</h4>
                                    <p className='text-sm text-gray-500 mb-3'>
                                        The paywall appears once the user hits <strong>either</strong> threshold. Set a value to 0 to disable that trigger.
                                    </p>

                                    <div className='space-y-4'>
                                        <div>
                                            <label className='block text-sm font-medium text-gray-700 mb-1'>
                                                Completed milestones
                                            </label>
                                            <div className='flex items-center gap-3'>
                                                <input
                                                    type='number'
                                                    min={0}
                                                    max={100}
                                                    value={formValues.usageThresholdMilestones ?? 1}
                                                    onChange={(e) => setFormValues({ ...formValues, usageThresholdMilestones: parseInt(e.target.value) || 0 })}
                                                    className='w-24 rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500'
                                                />
                                                <span className='text-sm text-gray-600'>milestones</span>
                                            </div>
                                            <p className='mt-1 text-xs text-gray-400'>
                                                Paywall triggers after completing this many milestones.
                                            </p>
                                        </div>

                                        <div>
                                            <label className='block text-sm font-medium text-gray-700 mb-1'>
                                                Chat conversations
                                            </label>
                                            <div className='flex items-center gap-3'>
                                                <input
                                                    type='number'
                                                    min={0}
                                                    max={1000}
                                                    value={formValues.usageThresholdChats ?? 5}
                                                    onChange={(e) => setFormValues({ ...formValues, usageThresholdChats: parseInt(e.target.value) || 0 })}
                                                    className='w-24 rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500'
                                                />
                                                <span className='text-sm text-gray-600'>conversations</span>
                                            </div>
                                            <p className='mt-1 text-xs text-gray-400'>
                                                Paywall triggers after this many chat conversations.
                                            </p>
                                        </div>
                                    </div>

                                    {(formValues.usageThresholdMilestones ?? 1) === 0 && (formValues.usageThresholdChats ?? 5) === 0 && (
                                        <div className='mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg'>
                                            <p className='text-sm text-amber-700 font-medium'>
                                                Both thresholds are set to 0. The paywall will never trigger — users will have unlimited free access.
                                            </p>
                                        </div>
                                    )}

                                    <div className='mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg'>
                                        <p className='text-sm text-blue-700'>
                                            In value-based mode, users see a friendlier paywall message ("You're off to a great start!") with no free trial — payment begins immediately upon subscribing.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {formValues.paywallEnabled && (
                        <div className='bg-white rounded-xl shadow-sm border border-neutral-200 p-6'>
                            <h3 className='text-lg font-semibold text-gray-900 mb-1'>Stripe Mode</h3>
                            <p className='text-sm text-gray-500 mb-4'>
                                Select which Stripe environment to use for payment processing. This is the default for all users — test users can override this individually.
                            </p>

                            {import.meta.env.VITE_ENVIRONMENT === 'dev' ? (
                                <div className='p-4 bg-gray-50 border border-gray-200 rounded-lg'>
                                    <div className='flex items-center gap-2 mb-2'>
                                        <div className='w-3 h-3 rounded-full bg-orange-400' />
                                        <span className='font-semibold text-gray-900'>Test Mode</span>
                                        <span className='text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full'>Dev only</span>
                                    </div>
                                    <p className='text-sm text-gray-500'>
                                        The development environment is restricted to Stripe test mode. Live mode is only available in production
                                        because Stripe webhook routing requires live-mode events to go exclusively to the production server to
                                        prevent duplicate charges and emails.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div className='flex gap-3'>
                                        <button
                                            type='button'
                                            onClick={() => setFormValues({ ...formValues, stripeMode: 'test' })}
                                            className={`flex-1 rounded-lg border-2 p-4 text-left transition-all ${
                                                formValues.stripeMode === 'test'
                                                    ? 'border-orange-400 bg-orange-50'
                                                    : 'border-neutral-200 bg-white hover:border-neutral-300'
                                            }`}
                                        >
                                            <div className='flex items-center gap-2 mb-1'>
                                                <div className={`w-3 h-3 rounded-full ${
                                                    formValues.stripeMode === 'test' ? 'bg-orange-400' : 'bg-neutral-300'
                                                }`} />
                                                <span className='font-semibold text-gray-900'>Test Mode</span>
                                            </div>
                                            <p className='text-sm text-gray-500'>
                                                Use Stripe test keys. No real charges. Use test card numbers.
                                            </p>
                                        </button>

                                        <button
                                            type='button'
                                            onClick={() => setFormValues({ ...formValues, stripeMode: 'live' })}
                                            className={`flex-1 rounded-lg border-2 p-4 text-left transition-all ${
                                                formValues.stripeMode === 'live'
                                                    ? 'border-green-500 bg-green-50'
                                                    : 'border-neutral-200 bg-white hover:border-neutral-300'
                                            }`}
                                        >
                                            <div className='flex items-center gap-2 mb-1'>
                                                <div className={`w-3 h-3 rounded-full ${
                                                    formValues.stripeMode === 'live' ? 'bg-green-500' : 'bg-neutral-300'
                                                }`} />
                                                <span className='font-semibold text-gray-900'>Live Mode</span>
                                            </div>
                                            <p className='text-sm text-gray-500'>
                                                Use real Stripe keys. Real charges will be processed.
                                            </p>
                                        </button>
                                    </div>

                                    {formValues.stripeMode === 'live' && (
                                        <div className='mt-3 p-3 bg-red-50 border border-red-200 rounded-lg'>
                                            <p className='text-sm text-red-700 font-medium'>
                                                Warning: Live mode will process real payments. Only enable for production use.
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    <div className='bg-white rounded-xl shadow-sm border border-neutral-200 p-6'>
                        <h3 className='text-lg font-semibold text-gray-900 mb-1'>Session Timeout</h3>
                        <p className='text-sm text-gray-500 mb-4'>
                            Users will be automatically logged out after this many hours. They will need to sign in again.
                        </p>
                        <div className='flex items-center gap-3'>
                            <input
                                type='number'
                                min={1}
                                max={720}
                                value={formValues.sessionTimeoutHours ?? 24}
                                onChange={(e) => setFormValues({ ...formValues, sessionTimeoutHours: parseInt(e.target.value) || 24 })}
                                className='w-24 rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500'
                            />
                            <span className='text-sm text-gray-600'>hours</span>
                        </div>
                        {(formValues.sessionTimeoutHours ?? 24) < 4 && (
                            <div className='mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg'>
                                <p className='text-sm text-amber-700 font-medium'>
                                    A short timeout may frustrate users who need to log in frequently.
                                </p>
                            </div>
                        )}
                        <p className='mt-2 text-xs text-gray-400'>
                            Default: 24 hours. Changes apply to new sessions only — existing sessions will expire at their original time.
                        </p>
                    </div>

                </div>
            )}
        </FormWrapper>
    )
}
